/** Learn F1 — Championship. Schema documented in gettingStarted.js. */
export const CHAMPIONSHIP = [
    {
        id: "points-system",
        title: "The Points System",
        level: "Beginner",
        readTime: 3,
        summary: "25 for a win down to 1 for tenth — plus sprint points on selected weekends.",
        keyTakeaway: "25-18-15-12-10-8-6-4-2-1 for the top ten; sprints pay 8 down to 1 for the top eight.",
        core: [
            "Grand Prix points: 25, 18, 15, 12, 10, 8, 6, 4, 2, 1 for positions 1–10. Sprint races award 8-7-6-5-4-3-2-1 to the top eight. The fastest-lap bonus point (for a top-ten finisher) existed 2019–2024 before being dropped. Shortened races pay reduced points on a sliding scale if fewer than 75% of laps complete.",
            "Both championships use the same race results: drivers keep their own points; a team's two cars sum for the constructors' table.",
        ],
        affectsRace: "The 25/18 gap (7 points) vs the 18/15 gap (3) shapes risk decisions: fighting for a win is worth more risk than fighting for second — and you'll see teams call off intra-team fights accordingly.",
        example: "The 2021 title finished 395.5 to 387.5 — the half-point from Spa's two-lap 'race' (half points, awarded controversially) genuinely mattered in the final margin.",
        misconception: "“Every points position is worth fighting equally hard for.” P10's single point rarely justifies tire-destroying battles — teams routinely surrender it to protect a strategy; P1 vs P2 justifies almost anything.",
        related: ["drivers-championship", "constructors-championship", "sprint-weekends", "chequered-flag"],
        visual: { type: "points-table" },
        video: {
            src: "/media/learn/points-system.mp4",
            poster: "/media/learn/points-system.jpg",
            orientation: "vertical",
        },
    },
    {
        id: "drivers-championship",
        title: "The Drivers' Championship",
        level: "Beginner",
        readTime: 3,
        summary: "The individual title: most points across the season, with wins as the tiebreaker.",
        keyTakeaway: "Ties break on countback: most wins, then most seconds, and so on down the results.",
        core: [
            "Every driver accumulates points personally, regardless of team switches mid-season. Equal points at season's end resolve by countback — most wins first, then most second places, continuing until the tie breaks.",
            "The title is officially decided when no rival can mathematically catch the leader; in lopsided seasons that can happen with several races to spare (Verstappen sealed 2022 in Japan with four rounds left).",
        ],
        affectsRace: "Championship position changes on-track behavior: a points leader defends conservatively against title rivals ('the championship-first move'), while mathematically-eliminated drivers race for pride and wins.",
        example: "Brazil 2008 remains the reference finale: Hamilton took the title by passing Glock for fifth place in the last corners of the last lap, seconds after Massa's home crowd had begun celebrating.",
        misconception: "“The best car's driver always wins the title.” Reliability, teammate dynamics, and consistency have handed titles to the second-fastest package many times — see 1982, when champion Rosberg won a single race.",
        related: ["points-system", "constructors-championship", "how-titles-are-won"],
        visual: null,
        video: {
            src: "/media/learn/drivers-championship.mp4",
            poster: "/media/learn/drivers-championship.jpg",
            orientation: "vertical",
        },
    },
    {
        id: "constructors-championship",
        title: "The Constructors' Championship",
        level: "Beginner",
        readTime: 3,
        summary: "The teams' title — both cars' points combined — and the one that decides the prize money.",
        keyTakeaway: "Constructors' position sets each team's share of F1's revenue: hundreds of millions ride on it.",
        core: [
            "Both cars score, so consistency across two drivers beats one superstar and a struggling teammate. The constructors' standings determine prize-fund distribution (plus historical bonuses), pit-lane garage order, and even wind-tunnel time under the sliding-scale aerodynamic testing rules — lower-placed teams get more development allowance.",
            "This is why teams sometimes prioritize the constructors' fight over a driver's personal position: two P4-P5 finishes usually beat a P2 and a DNF.",
        ],
        affectsRace: "Late-season team orders and strategy splitting make sense through this lens: maximizing *combined* points is the employer's actual objective.",
        example: "In 2021 Red Bull's Verstappen won the drivers' title but Mercedes took the constructors' — split outcomes like that (also 1999, 2008) show they're genuinely different competitions.",
        misconception: "“The constructors' title is a consolation prize.” Inside the paddock it's the financial championship — team budgets, staff bonuses, and development resources all hang on it.",
        related: ["points-system", "drivers-championship", "cost-cap", "teams-and-drivers"],
        visual: null,
        video: {
            src: "/media/learn/constructors-championship.mp4",
            poster: "/media/learn/constructors-championship.jpg",
            orientation: "vertical",
        },
    },
    {
        id: "how-titles-are-won",
        title: "How Titles Are Actually Won",
        level: "Intermediate",
        readTime: 3,
        summary: "Championships are lost more than won: reliability, bad weekends, and development races decide them.",
        keyTakeaway: "Over a 24-race season, the floor of your bad weekends matters more than the ceiling of your good ones.",
        core: [
            "Title math rewards relentless scoring. A DNF costs ~20 points against a rival's win — two of those need four dominant weekends to repay. So championship teams obsess over reliability, pit-crew consistency, and 'bringing it home' on bad days.",
            "The in-season development race is the other decider: the car that starts fastest rarely finishes the season fastest. Upgrade efficiency — performance gained per wind-tunnel hour and per cost-cap dollar — has become the defining team skill.",
        ],
        affectsRace: "Watch title contenders' behavior shift late-season: bigger gaps left in wheel-to-wheel moments, more conservative strategy — protecting a 30-point lead is a different sport from chasing one.",
        example: "2007: Räikkönen took the title by one point after McLaren's pair (Hamilton/Alonso) split wins and imploded politically — consistency plus rivals' chaos beat outright pace.",
        misconception: "“Titles are decided in finales.” Blockbuster deciders are rare; most titles are decided by mid-season DNFs and upgrade swings that only look decisive in hindsight.",
        related: ["drivers-championship", "constructors-championship", "cost-cap", "points-system"],
        visual: null,
        video: {
            src: "/media/learn/how-titles-are-won.mp4",
            poster: "/media/learn/how-titles-are-won.jpg",
            orientation: "vertical",
        },
    },
    {
        id: "cost-cap",
        title: "The Cost Cap & Development War",
        level: "Intermediate",
        readTime: 3,
        summary: "A ~$135M annual spending limit turned F1's arms race from 'who spends most' into 'who spends best'.",
        keyTakeaway: "Since 2021, efficiency is the meta-game: every upgrade competes for budget against crash repairs and next year's car.",
        core: [
            "The cost cap (introduced at $145M in 2021, tightening since, with exclusions like driver salaries and top-three staff) limits what teams spend on performance. Alongside it, aerodynamic testing restrictions scale by championship position — leaders get less wind-tunnel time than backmarkers.",
            "Consequences ripple everywhere: crash damage now has strategic cost (a big shunt literally deletes future upgrades), and mid-season development must be rationed against the next season's car project.",
        ],
        affectsRace: "When a driver crashes heavily, the commentary about 'cost cap implications' is real — teams have publicly cited crash-repair spending as a reason for slowed development.",
        example: "Red Bull's 2021 minor breach (~$2M overspend) drew a $7M fine and a 10% wind-tunnel cut — a penalty many rivals argued was worth trading for a championship.",
        misconception: "“The cap equalized budgets overnight.” Big teams retain structural advantages (facilities, people, know-how) — the cap narrows the gap gradually, not instantly.",
        related: ["constructors-championship", "how-titles-are-won", "scrutineering"],
        visual: null,
        video: {
            src: "/media/learn/cost-cap.mp4",
            poster: "/media/learn/cost-cap.jpg",
            orientation: "horizontal",
        },
    },
];
